import { Card, CardProps, SxProps, Theme } from '@mui/material';
import { LocationDescriptor } from 'history';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { appCardInteractiveSx, appCardLinkStyle, appCardRootSx } from './app-card-styles';

/** Same as `react-router-dom` `Link` `to`: `history` `LocationDescriptor` (string or location object). */
export type AppCardRouterTo = LocationDescriptor;

export interface AppCardProps extends Omit<CardProps, 'sx' | 'children'> {
  /** Top media slot (poster, waveform, preview). Rendered before `children`. */
  media?: ReactNode;
  /** Main content (often `AppCardContent`). */
  children?: ReactNode;
  /** Optional block below main content (e.g. creator row, tags). */
  footer?: ReactNode;
  /** Optional bottom actions (often `AppCardActions`). */
  actions?: ReactNode;
  /**
   * Internal navigation: when set, wraps the card in `react-router-dom` `Link`.
   * Uses the same `to` shape as `Link` (`history` `LocationDescriptor`). Takes precedence over `href` when both are provided.
   */
  to?: AppCardRouterTo;
  /** External URL: wraps the card in `<a>` when `to` is not set. */
  href?: string;
  /** `target` for external anchor (e.g. `_blank`). */
  linkTarget?: string;
  /** `rel` for external links; defaults to `noopener noreferrer` when `linkTarget` is `_blank`. */
  linkRel?: string;
  /**
   * When true, applies hover border/shadow styles.
   * Defaults to true if `to`, `href`, or `onClick` is provided on the card.
   */
  interactive?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Reusable card shell: optional media, body, footer, and actions with consistent layout.
 * Supports non-clickable cards, router links, external links, or `onClick` on the `Card`.
 */
export function AppCard({
  media,
  footer,
  actions,
  children,
  to,
  href,
  linkTarget,
  linkRel = 'noopener noreferrer',
  interactive: interactiveProp,
  sx,
  onClick,
  ...cardProps
}: AppCardProps) {
  const hasRouterLink =
    to &&
    (typeof to === 'string'
      ? to.length > 0
      : Boolean(to.pathname?.length || to.search?.length || to.hash?.length));
  const hasExternalLink = !hasRouterLink && typeof href === 'string' && href.length > 0;
  const interactive =
    interactiveProp ?? !!(hasRouterLink || hasExternalLink || typeof onClick === 'function');

  const card = (
    <Card
      {...cardProps}
      onClick={onClick}
      sx={
        [
          appCardRootSx,
          ...(interactive ? [appCardInteractiveSx] : []),
          ...(sx !== undefined ? (Array.isArray(sx) ? sx : [sx]) : []),
        ] as SxProps<Theme>
      }
    >
      {media}
      {children}
      {footer}
      {actions}
    </Card>
  );

  if (hasRouterLink) {
    return (
      <Link to={to} style={appCardLinkStyle}>
        {card}
      </Link>
    );
  }

  if (hasExternalLink) {
    return (
      <a
        href={href}
        target={linkTarget}
        rel={linkTarget === '_blank' ? linkRel : undefined}
        style={appCardLinkStyle}
      >
        {card}
      </a>
    );
  }

  return card;
}
